import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { z } from 'zod'
import { DiscordIcon, Field, FormAlert } from '@/components/app/form'
import { CaptchaSubmit, useCaptcha } from '@/components/app/turnstile'
import { authClient, authErrorMessage } from '@/lib/auth-client'
import { configQuery } from '@/lib/queries'
import { safeRedirect } from '@/lib/session'
import { DiscordButton, OrDivider } from './-auth-shared'

export const Route = createFileRoute('/_auth/sign-in')({
  validateSearch: z.object({ redirect: z.string().optional(), verified: z.boolean().optional() }),
  head: () => ({ meta: [{ title: 'Sign in · Nebueye' }] }),
  component: SignIn,
})

function SignIn() {
  const search = Route.useSearch()
  const router = useRouter()
  const queryClient = useQueryClient()
  const inviteOnly = useQuery(configQuery()).data?.signups === 'invite'
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  const captcha = useCaptcha('sign-in')

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    setPending(true)
    setError(undefined)
    const { data, error } = await authClient.signIn.email({
      email: String(form.get('email')),
      password: String(form.get('password')),
      rememberMe: true,
      fetchOptions: { headers: captcha.headers },
    })
    // The token was spent on that attempt, whatever came of it.
    captcha.reset()
    setPending(false)
    if (error) return setError(authErrorMessage(error))
    // Right password, but the account has an authenticator app: no session yet.
    if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
      return router.navigate({ to: '/two-factor', search: { redirect: search.redirect } })
    }
    await queryClient.invalidateQueries()
    // The target may carry its own query string (e.g. /device?user_code=…).
    router.history.push(safeRedirect(search.redirect))
  }

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">Welcome back</h1>
        <p className="text-muted-foreground text-[15px]">Sign in to see what went through.</p>
      </div>

      {search.verified && <FormAlert tone="success">Email confirmed. Sign in to continue.</FormAlert>}

      <DiscordButton callbackURL={safeRedirect(search.redirect)} label="Continue with Discord" icon={<DiscordIcon className="size-4" />} />
      <OrDivider />

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && <FormAlert>{error}</FormAlert>}
        <Field label="Email" name="email" type="email" autoComplete="email" required autoFocus />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          aside={
            <Link to="/forgot-password" className="text-primary text-[13px] font-medium hover:underline">
              Forgot password?
            </Link>
          }
        />
        {captcha.widget}
        <CaptchaSubmit captcha={captcha} pending={pending}>
          Sign in
        </CaptchaSubmit>
      </form>

      {inviteOnly ? (
        <p className="text-muted-foreground text-sm">Nebueye is invite-only right now. Got an invitation? Open the link in that email to create your account.</p>
      ) : (
        <p className="text-muted-foreground text-sm">
          New to Nebueye?{' '}
          <Link to="/sign-up" search={{ redirect: search.redirect }} className="text-primary font-medium hover:underline">
            Create an account
          </Link>
        </p>
      )}
    </div>
  )
}
